import { isHash, zeroAddress } from 'viem'

import { toCursorPage } from '@/helpers/cursorPage'
import { orpc } from '@/lib/orpc/client'
import { errorCodeOf } from '@/lib/orpc/errors'

import type {
  MarketSupplier,
  MarketSuppliersInput,
  MarketSuppliersOutput
} from '../suppliers.contract'

/** The procedure's input without the cursor; `marketId` is the page's route param. */
export type MarketSuppliersFilters = Omit<MarketSuppliersInput, 'cursor' | 'marketId'> & {
  marketId: string
}

/** A supplier row with the loan asset every row of its page shares. */
export type MarketSupplierItem = MarketSupplier & Pick<MarketSuppliersOutput, 'loanAsset'>

/** A market the bridge does not know has no suppliers: the table shows its empty state. */
export const EMPTY_MARKET_SUPPLIERS: MarketSuppliersOutput = {
  items: [],
  partial: false,
  nextCursor: null,
  countTotal: 0,
  loanAsset: { address: zeroAddress, decimals: 18, symbol: null, logoUri: null }
}

export const toMarketSuppliersPage = ({ items, loanAsset, ...page }: MarketSuppliersOutput) => ({
  ...toCursorPage({
    ...page,
    items: items.map((item): MarketSupplierItem => ({ ...item, loanAsset }))
  }),
  countTotal: page.countTotal
})

export const marketSuppliersTableQueryKey = (filters: MarketSuppliersFilters) =>
  [...orpc.market.suppliers.key({ type: 'infinite' }), filters] as const

export async function fetchMarketSuppliersPage(
  filters: MarketSuppliersFilters,
  cursor: string | undefined
) {
  const { marketId } = filters
  if (!isHash(marketId)) return toMarketSuppliersPage(EMPTY_MARKET_SUPPLIERS)
  try {
    return toMarketSuppliersPage(await orpc.market.suppliers.call({ ...filters, marketId, cursor }))
  } catch (error) {
    if (errorCodeOf(error) === 'NOT_FOUND') return toMarketSuppliersPage(EMPTY_MARKET_SUPPLIERS)
    throw error
  }
}
